import React, { createContext, useContext, useState, useEffect } from 'react';
import { User } from '@supabase/supabase-js';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { isNetworkError } from '../utils/api/networkErrors';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;

// Define the context type
interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

// Create the context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [refreshAttempted, setRefreshAttempted] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isPublicRoute = (pathname: string) => {
    return ['/login', '/create-account', '/', '/reset-password', '/business'].includes(pathname);
  };

  useEffect(() => {
    let mounted = true;

    const checkAdminStatus = async (userId: string) => {
      try {
        const { data: profile, error } = await supabase
          .from('user_profiles')
          .select('is_admin')
          .eq('id', userId)
          .single();

        if (error) throw error;
        return !!profile?.is_admin;
      } catch (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
    };

    const initializeAuth = async () => {
      try {
        // Try to get session with retries
        for (let i = 0; i < MAX_RETRIES; i++) {
          try {
            const result = await supabase.auth.getSession();
            if (!result.error) {
              setSessionChecked(true);
              setUser(result.data.session?.user ?? null);
              
              if (result.data.session?.user) {
                const isUserAdmin = await checkAdminStatus(result.data.session.user.id);
                setIsAdmin(isUserAdmin);
              } else if (!isPublicRoute(location.pathname)) {
                navigate('/login', { replace: true });
              }
              
              setIsLoading(false);
              setRefreshAttempted(true);
              return;
            }
            if (!isNetworkError(result.error)) throw result.error;
          } catch (e) {
            if (!isNetworkError(e)) throw e;
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * Math.pow(2, i)));
          }
        }
        throw new Error('Failed to initialize auth after retries');
      } catch (error) {
        console.error('Auth initialization error:', error);
        toast.error('Having trouble connecting. Please check your internet connection.');
      } finally {
        if (mounted) {
          setIsLoading(false);
          setRefreshAttempted(true);
          setSessionChecked(true);
        }
      }
    };

    initializeAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (mounted) {
        setUser(session?.user ?? null);
        
        if (session?.user) {
          const isUserAdmin = await checkAdminStatus(session.user.id);
          setIsAdmin(isUserAdmin);
        } else if (event === 'SIGNED_OUT' && !isPublicRoute(location.pathname)) {
          navigate('/login', { replace: true });
        }
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setIsAdmin(false);
      // Clear any cached data
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error('Failed to sign out');
    }
  };

  const value = {
    user,
    isAuthenticated: !!user && sessionChecked,
    isAdmin,
    isLoading: isLoading || !sessionChecked,
    signOut: handleSignOut
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Export the hook
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };